import React from "react";
import { motion } from "framer-motion";

const FEATURES = [
  {
    icon: "fa-book-open",
    color: "from-blue-400 to-[#47b3fe]",
    bg: "bg-blue-50",
    title: "AI-Powered Summaries",
    desc: "Concise summaries of complex topics help you grasp key concepts in minutes, not hours.",
  },
  {
    icon: "fa-circle-question",
    color: "from-indigo-400 to-indigo-600",
    bg: "bg-indigo-50",
    title: "Interactive Quizzes",
    desc: "Custom quizzes generated from your own materials so you can test what you actually need.",
  },
  {
    icon: "fa-clone",
    color: "from-violet-400 to-violet-600",
    bg: "bg-violet-50",
    title: "Smart Flashcards",
    desc: "Spaced-repetition flashcards that focus on the details you're most likely to forget.",
  },
];

const ShowcaseSection: React.FC = () => {
  return (
    <section
      id="features"
      className="bg-[#eef2f7] pb-16 pt-28 md:pb-24 md:pt-44"
    >
      <div className="container mx-auto max-w-6xl px-5 md:px-8">
        {/* Heading */}
        <div className="mb-10 text-center md:mb-12">
          <motion.h2
            className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Learn faster. Remember longer.
          </motion.h2>
          <motion.p
            className="mt-3 text-slate-500"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Three tools that do the heavy lifting for you.
          </motion.p>
        </div>

        {/* Feature cards */}
        <div className="grid gap-6 sm:grid-cols-3">
          {FEATURES.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="rounded-[24px] border border-white/70 bg-white p-7 shadow-[0_18px_40px_-24px_rgba(15,23,42,0.2)]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div
                className={`mb-5 flex h-11 w-11 items-center justify-center rounded-xl ${feature.bg}`}
              >
                <i
                  className={`fas ${feature.icon} bg-gradient-to-br bg-clip-text text-lg text-transparent ${feature.color}`}
                />
              </div>
              <h3 className="mb-2 text-lg font-semibold tracking-tight text-slate-900">
                {feature.title}
              </h3>
              <p className="text-sm leading-relaxed text-slate-500">
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ShowcaseSection;
